"use client";

import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';
import { toolRegistry } from '@/lib/tool-registry';

export default function SEOIndex() {
    const { t } = useLanguage();

    const tools = Object.values(toolRegistry);
    const categories = Array.from(new Set(tools.map(t => t.config.id.includes('pdf') ? 'PDF Tools' : t.metadata.title.split('|')[1]?.trim() || 'Utilities')));

    const toolCategories = [
        {
            name: 'Data & Developer',
            tools: tools.filter(t => t.config.id.match(/json|xml|yaml|sql|jwt/)).map(t => ({
                name: t.config.name,
                path: `/tools/${t.config.id}`,
                keywords: t.config.description
            }))
        },
        {
            name: 'Text & Content',
            tools: tools.filter(t => t.config.id.match(/text|word|list|slug|case|grammar|remove/)).map(t => ({
                name: t.config.name,
                path: `/tools/${t.config.id}`,
                keywords: t.config.description
            }))
        },
        {
            name: 'Security & Marketing',
            tools: tools.filter(t => t.config.id.match(/password|username|generator|analyzer|optimizer|roi/)).map(t => ({
                name: t.config.name,
                path: `/tools/${t.config.id}`,
                keywords: t.config.description
            }))
        }
    ].filter(c => c.tools.length > 0);

    const renderCategory = (cat: typeof toolCategories[0]) => (
        <div key={cat.name} className="seo-category-block">
            <div style={{
                fontSize: '1.25rem',
                color: '#0f172a',
                marginBottom: '1.5rem',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                borderLeft: '4px solid var(--primary)',
                paddingLeft: '0.75rem'
            }}>
                {cat.name}
            </div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '1rem', padding: 0 }}>
                {cat.tools.map((tool, tIdx) => (
                    <li key={tIdx} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                        <Link
                            href={tool.path}
                            style={{
                                textDecoration: 'none',
                                color: '#2563eb',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                display: 'block',
                                marginBottom: '0.2rem',
                                transition: 'color 0.2s'
                            }}
                            className="hover-underline"
                        >
                            {tool.name}
                        </Link>
                        <p style={{
                            fontSize: '0.8rem',
                            color: '#64748b',
                            margin: 0,
                            lineHeight: '1.5',
                            fontWeight: 500
                        }}>
                            {tool.keywords.length > 60 ? tool.keywords.substring(0, 60) + '...' : tool.keywords}
                        </p>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <section style={{ paddingTop: 'clamp(2rem, 5vh, 3.5rem)', borderTop: '1px solid #f1f5f9', marginTop: 'clamp(1rem, 3vh, 2rem)' }}>
            <h2 style={{ fontSize: 'var(--fs-3xl)', marginBottom: 'clamp(3.5rem, 8vh, 5rem)', fontWeight: 900, letterSpacing: '-0.03em', textAlign: 'center' }}>
                {t('explore_ecosystem_pre') || 'Explore the'} <span style={{ color: 'var(--primary)' }}>{t('explore_ecosystem_highlight') || 'Ecosystem'}</span>
            </h2>

            {/* Desktop 3-Column Grid (min-width: 1200px) */}
            <div className="seo-grid-desktop">
                {toolCategories[0] && renderCategory(toolCategories[0])}
                {toolCategories[1] && renderCategory(toolCategories[1])}
                {toolCategories[2] && renderCategory(toolCategories[2])}
            </div>

            {/* Tablet 2-Column Grid (min-width: 768px and max-width: 1199px) */}
            <div className="seo-grid-tablet">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
                    {toolCategories[0] && renderCategory(toolCategories[0])}
                    {toolCategories[2] && renderCategory(toolCategories[2])}
                </div>
                <div>
                    {toolCategories[1] && renderCategory(toolCategories[1])}
                </div>
            </div>

            {/* Mobile 1-Column Grid (max-width: 767px) */}
            <div className="seo-grid-mobile">
                {toolCategories[0] && renderCategory(toolCategories[0])}
                {toolCategories[1] && renderCategory(toolCategories[1])}
                {toolCategories[2] && renderCategory(toolCategories[2])}
            </div>

            <div style={{ marginTop: '5rem' }}>
                <div style={{ fontSize: '1.25rem', marginBottom: '2rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center' }}>
                    {t('trending_tools_title') || 'Trending Search Tools'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                    {[
                        { slug: 'alphabetical-list-sorter', name: "Alphabetical List Sorter" },
                        { slug: 'reading-time-calculator', name: "Reading Time Calculator" },
                        { slug: 'secure-password-vault-gen', name: "Secure Password Generator" },
                        { slug: 'json-beautifier-online', name: "JSON Beautifier Online" },
                        { slug: 'alphabetical-keyword-sorter', name: "Keyword Sorting Tool (A–Z)" },
                        { slug: 'email-finder-from-text', name: "Extract Emails from Text" },
                        { slug: 'remove-duplicatelines-free', name: "Remove Duplicate Lines (Free Tool)" },
                        { slug: 'html-to-text-converter', name: "HTML to Text Converter" }
                    ].map(tool => (
                        <Link
                            key={tool.slug}
                            href={`/tools/${tool.slug}`}
                            style={{
                                padding: '0.75rem 1.75rem',
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '100px',
                                textDecoration: 'none',
                                color: '#475569',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                            className="hover-lift"
                        >
                            {tool.name}
                        </Link>
                    ))}
                </div>
            </div>

            <p style={{ marginTop: '4rem', textAlign: 'center', fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>
                {t('sitemap_desc') || 'Explore all tools and articles in our comprehensive sitemap.'}
            </p>
        </section>
    );
}
